import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react';
import Chart from 'chart.js';
import { merge } from 'lodash';

type Config = Chart['config'];

export const ChartComponent = ({ config }: { config: Config }) => {
    const { canvasRef } = useChart(config);
    return <canvas ref={canvasRef}></canvas>;
};

function useChart(config: Config) {
    const chart = useRef<Chart>();
    const configRef = useRef<Config>(config);

    configRef.current = useMemo<Config>(() => {
        return merge(configRef.current, config);
    }, [config]);

    useEffect(() => {
        chart.current?.update();
    }, [config]);

    const canvasRef = useCallback((canvas: HTMLCanvasElement) => {
        if (canvas) {
            chart.current?.destroy();
            chart.current = new Chart(canvas, configRef.current);
        }
    }, []);

    return { canvasRef };
}

export interface Props extends React.CanvasHTMLAttributes<HTMLCanvasElement> {
    id?: string;
    className?: string;
    height?: number;
    width?: number;
    redraw?: boolean;
    type: Chart.ChartType;
    data: Chart.ChartData;
    options?: Chart.ChartOptions;
    fallbackContent?: React.ReactNode;
    plugins?: Chart.PluginServiceRegistrationOptions[];
}

export const BetterChartComponent = forwardRef<Chart | undefined, Props>((props, ref) => {
    const {
        id,
        className,
        height = 400,
        width = 300,
        redraw = false,
        type,
        data,
        options = {},
        plugins = [],
        fallbackContent,
        ...rest
    } = props;

    const canvas = useRef<HTMLCanvasElement>(null);

    const [chart, setChart] = useState<Chart>();

    useImperativeHandle<Chart | undefined, Chart | undefined>(ref, () => chart, [chart]);

    const renderChart = useCallback(() => {
        if (!canvas.current) return;

        setChart(
            new Chart(canvas.current, {
                type,
                data: data,
                options,
                plugins,
            }),
        );
    }, [type, data, options, plugins]);

    const updateChart = useCallback(() => {
        if (!chart) return;

        if (options) {
            chart.options = { ...options };
        }

        if (!chart.config.data) {
            chart.config.data = data;
            chart.update();
            return;
        }

        const { datasets: newDataSets = [], ...newChartData } = data;

        // copy values other than the datasets
        Object.assign(chart.config.data, newChartData);

        chart.config.data.datasets = newDataSets;

        chart.update();
    }, [chart, data, options]);

    const destroyChart = useCallback(() => {
        if (chart) chart.destroy();
    }, [chart]);

    useEffect(() => {
        renderChart();

        return () => destroyChart();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (redraw) {
            destroyChart();
            setTimeout(() => {
                renderChart();
            }, 0);
        } else {
            updateChart();
        }
    }, [destroyChart, renderChart, updateChart, redraw]);

    return (
        <canvas
            {...rest}
            height={height}
            width={width}
            ref={canvas}
            id={id}
            className={className}
            // onClick={onClick}
            data-testid="canvas"
            role="img"
        >
            {fallbackContent}
        </canvas>
    );
});
